// src/components/layout/Dashboard/Users/UsersTable.jsx
import { useState, useMemo, useEffect, useCallback, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { serverURL, postData, getData } from "../../../../services/Fetchnodeservices";
import { ThemeContext } from "../../../../context/ThemeContext";

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT LIBRARIES - Install: npm install jspdf jspdf-autotable xlsx file-saver
// ─────────────────────────────────────────────────────────────────────────────
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// ─────────────────────────────────────────────────────────────────────────────
// API FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

const apiGetUsers = async ({ page, perPage, search }) => {
  try {
    const response = await getData("users/Users");
    let users = response.data || [];
    
    const transformedUsers = users.map((user, index) => {
      let imageUrl = null;
      if (user.pic && user.pic.trim() !== "") {
        let cleanPic = user.pic;
        if (cleanPic.includes('/')) {
          cleanPic = cleanPic.split('/').pop();
        }
        imageUrl = `${serverURL}/public/uploadnew/upload/${cleanPic}`;
      }
      
      return {
        id: user.id.toString(),
        sn: index + 1,
        status: user.status === "1" ? "Active" : user.status === "0" ? "Inactive" : "Blocked",
        date: user.created_at ? user.created_at.split(' ')[0] : new Date().toISOString().split('T')[0],
        name: user.name || "Unknown",
        contact: user.phone || "N/A",
        wallet: `₹${parseFloat(user.wallet || 0).toLocaleString()}`,
        vip: user.is_vip === "1" ? "Yes" : "No",
        avatar: imageUrl,
        email: user.email,
        latitude: user.latitude,
        longitude: user.longitude,
        address: user.address,
        rawWallet: user.wallet,
        rawStatus: user.status,
        rawVip: user.is_vip,
        fcmtoken: user.fcmtoken,
        myreferal: user.myreferal,
        refer_by: user.refer_by,
        rawDate: user.created_at,
        statusRaw: user.status
      };
    });
    
    let filteredUsers = [...transformedUsers];
    if (search) {
      const searchLower = search.toLowerCase();
      filteredUsers = filteredUsers.filter(u => 
        u.name.toLowerCase().includes(searchLower) ||
        u.contact.includes(search) ||
        u.email?.toLowerCase().includes(searchLower) ||
        u.id.toLowerCase().includes(searchLower)
      );
    }
    
    const start = (page - 1) * perPage;
    const paginatedUsers = filteredUsers.slice(start, start + perPage);
    
    return { data: paginatedUsers, total: filteredUsers.length, allData: filteredUsers };
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

const apiUpdateUser = async (id, payload) => {
  console.log("Updating user:", id, payload);
  return { success: true };
};

const apiDeleteUsers = async (ids) => {
  console.log("Deleting users:", ids);
  return { success: true };
};

const apiGetUser = async (id) => {
  try {
    const response = await getData(`users/User/${id}`);
    return response.data || null;
  } catch (error) {
    console.error("Error fetching user:", error);
    return null;
  }
};

const apiGetUserOrders = async (userId) => {
  try {
    const response = await postData("users/ordersbyid", { userid: userId });
    if (response.status === true && response.data) {
      if (response.data.length > 0 && response.data[0].total_orders !== undefined) {
        return { orders: [], totalOrders: response.data[0].total_orders };
      }
      return { orders: response.data, totalOrders: response.data.length };
    }
    return { orders: [], totalOrders: 0 };
  } catch (error) {
    console.error("Error fetching user orders:", error);
    throw error;
  }
};

const apiGetUserComplaints = async (userId) => {
  return [];
};

const apiUpdateComplaintStatus = async (complaintId, status) => {
  console.log("Updating complaint:", complaintId, status);
  return { success: true };
};

const apiSendPushNotification = async (userId, title, body, data = {}) => {
  console.log("Sending notification to:", userId, { title, body });
  return { success: true };
};

const apiSendBulkNotification = async (userIds, title, body, data = {}) => {
  console.log("Sending bulk notification to:", userIds, { title, body });
  return { success: true };
};

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const EyeIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>);
const EditIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const SearchIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);
const ChevronLeftIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>);
const ChevronRightIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>);
const TrashIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>);
const RefreshIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>);
const SpinnerIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" style={{ animation: "spin 0.8s linear infinite", transformOrigin: "center" }}/></svg>);
const ShoppingBagIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>);
const PDFIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>);
const ExcelIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="16" x2="16" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="8" x2="10" y2="8"/></svg>);
const DownloadIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>);
const ChevronDownIcon = () => (<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>);

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS (Theme-Aware)
// ─────────────────────────────────────────────────────────────────────────────

const SmartAvatar = ({ name, src, size = 28, theme, borderColor }) => {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const initials = name?.split(" ").map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  const colors = [theme.primary, theme.textSecondary, theme.textPrimary, theme.success, theme.warning];
  const colorIndex = (name?.charCodeAt(0) || 0) % colors.length;
  const finalBorderColor = borderColor || theme.border;

  if (!src || error) {
    return (
      <div style={{ 
        width: size, 
        height: size, 
        borderRadius: "50%", 
        background: colors[colorIndex], 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        fontSize: size * 0.34, 
        fontWeight: 700, 
        color: "#fff", 
        fontFamily: "'DM Sans', sans-serif", 
        flexShrink: 0, 
        border: `1px solid ${finalBorderColor}` 
      }}>
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      onLoad={() => setIsLoading(false)}
      onError={(e) => {
        console.error("Image failed to load:", src);
        setError(true);
        setIsLoading(false);
      }}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        border: `1px solid ${finalBorderColor}`,
        flexShrink: 0,
        display: isLoading ? "none" : "block"
      }}
    />
  );
};

const Avatar = ({ name = "?", size = 28, theme }) => {
  const initials = name.split(" ").map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const colors = [theme.primary, theme.textSecondary, theme.textPrimary, theme.success, theme.warning];
  const colorIndex = (name.charCodeAt(0) || 0) % colors.length;
  const borderColor = theme.border;
  return (<div style={{ width: size, height: size, borderRadius: "50%", background: colors[colorIndex], display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.34, fontWeight: 700, color: theme.textMuted, fontFamily: "'DM Sans', sans-serif", flexShrink: 0, border: `1px solid ${borderColor}` }}>{initials || "?"}</div>);
};

const StatusBadge = ({ status, theme }) => {
  const config = { 
    Active: { bg: `${theme.success}26`, color: theme.success, border: `${theme.success}40`, dot: theme.success }, 
    Inactive: { bg: `${theme.textMuted}26`, color: theme.textMuted, border: `${theme.textMuted}40`, dot: theme.textMuted }, 
    Blocked: { bg: `${theme.error}26`, color: theme.error, border: `${theme.error}40`, dot: theme.error } 
  };
  const c = config[status] || config.Inactive;
  return (<div style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px 2px 6px", borderRadius: "16px", background: c.bg, border: `1px solid ${c.border}` }}><div style={{ width: "4px", height: "4px", borderRadius: "50%", background: c.dot }} /><span style={{ fontSize: "9px", fontWeight: 600, color: c.color, fontFamily: "'DM Sans', sans-serif" }}>{status}</span></div>);
};

const Checkbox = ({ checked, indeterminate, onChange, theme }) => {
  const borderColor = checked || indeterminate ? theme.primary : theme.border;
  return (<div onClick={onChange} style={{ width: "14px", height: "14px", borderRadius: "3px", flexShrink: 0, border: `2px solid ${borderColor}`, background: checked || indeterminate ? theme.primary : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s ease" }}>{indeterminate && !checked ? <div style={{ width: "7px", height: "2px", background: "#fff", borderRadius: "1px" }} /> : checked ? <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> : null}</div>);
};

const ColHeader = ({ children, theme, width }) => {
  const color = theme.primary;
  const borderColor = theme.border;
  return (<th style={{ padding: "10px 8px", textAlign: "left", fontSize: "10px", fontWeight: 700, letterSpacing: "0.4px", color: color, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap", background: "transparent", borderBottom: `1px solid ${borderColor}`, userSelect: "none", width: width || "auto" }}>{children}</th>);
};

const SkeletonRow = ({ cols, theme }) => {
  const bgGradient = `linear-gradient(90deg, ${theme.border} 25%, ${theme.surface} 50%, ${theme.border} 75%)`;
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} style={{ padding: "10px 8px" }}>
          <div
            style={{
              height: "10px",
              borderRadius: "4px",
              background: bgGradient,
              backgroundSize: "200% 100%",
              animation: "shimmer 1.4s infinite",
              width: i === 0 ? "14px" : i === 4 ? "28px" : "60%",
            }}
          />
        </td>
      ))}
    </tr>
  );
};

const Toast = ({ message, type, onClose, theme }) => {
  const bg = type === "error" ? `${theme.error}1a` : `${theme.success}1a`;
  const border = type === "error" ? `${theme.error}40` : `${theme.success}40`;
  const textColor = type === "error" ? theme.error : theme.success;
  return (<div style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 999, background: bg, border: `1px solid ${border}`, borderRadius: "8px", padding: "10px 16px", display: "flex", alignItems: "center", gap: "8px", boxShadow: `0 8px 32px ${theme.primaryLight}`, animation: "slideUp 0.25s ease", maxWidth: "300px" }}><div style={{ width: "6px", height: "6px", borderRadius: "50%", background: type === "error" ? theme.error : theme.success, flexShrink: 0 }} /><span style={{ fontSize: "12px", color: textColor, fontFamily: "'DM Sans', sans-serif", flex: 1 }}>{message}</span><span onClick={onClose} style={{ cursor: "pointer", color: theme.textMuted, fontSize: "14px", lineHeight: 1 }}>×</span></div>);
};

// ─────────────────────────────────────────────────────────────────────────────
// DROPDOWN MENU COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const DropdownMenu = ({ trigger, children, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", display: "inline-block" }}>
      <div onClick={() => setIsOpen(!isOpen)} style={{ cursor: "pointer" }}>
        {trigger}
      </div>
      {isOpen && (
        <div style={{
          position: "absolute",
          top: "100%",
          right: 0,
          marginTop: "4px",
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: "8px",
          boxShadow: `0 8px 32px ${theme.primaryLight}`,
          minWidth: "180px",
          zIndex: 100,
          overflow: "hidden"
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

const DropdownItem = ({ onClick, children, theme, icon }) => (
  <div
    onClick={onClick}
    style={{
      padding: "8px 14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "11px",
      color: theme.textPrimary,
      cursor: "pointer",
      transition: "all 0.15s ease",
      fontFamily: "'DM Sans', sans-serif"
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = theme.primaryLight;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
    }}
  >
    {icon && <span style={{ color: theme.primary }}>{icon}</span>}
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT - UsersTable
// ─────────────────────────────────────────────────────────────────────────────
const UsersTable = () => {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  
  // ✅ Get permissions from localStorage
  const getPermissions = () => {
    try {
      const perms = localStorage.getItem('userPermission') || localStorage.getItem('userPermissions');
      if (perms) {
        return JSON.parse(perms);
      }
      return [];
    } catch (error) {
      console.error("Error parsing permissions:", error);
      return [];
    }
  };

  // ✅ Check if user has specific permission
  const hasPermission = (permission) => {
    const permissions = getPermissions();
    return permissions.includes(permission);
  };

  // ✅ Check permissions
  const canEditUser = hasPermission('users.edit');
  const canViewOrderHistory = hasPermission('users.OrderHistory');
  const canExport = hasPermission('users.export') || hasPermission('users.view') || true;

  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [viewUser, setViewUser] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOption, setExportOption] = useState('current'); // 'current' or 'all'

  const showToast = (message, type = "success") => { setToast({ message, type }); setTimeout(() => setToast(null), 3500); };

  const handleEditNavigation = (user) => {
    if (canEditUser) {
      navigate("/dashboard/users/edit", { state: { user: user } });
    } else {
      showToast("You don't have permission to edit users", "error");
    }
  };

  useEffect(() => { const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 500); return () => clearTimeout(t); }, [search]);

  const fetchUsers = useCallback(async () => {
    setLoading(true); 
    setError(null);
    try {
      const res = await apiGetUsers({ page, perPage, search: debouncedSearch });
      setUsers(res.data || []); 
      setAllUsers(res.allData || []);
      setTotal(res.total || 0);
    } catch (err) { 
      setError(err.message); 
      showToast(`Failed to load users: ${err.message}`, "error"); 
    } finally { 
      setLoading(false); 
    }
  }, [page, perPage, debouncedSearch]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ─── EXPORT FUNCTIONS ───
  const getExportData = (dataToExport) => {
    return dataToExport.map(u => ({
      'User ID': u.id || '',
      'Name': u.name || 'N/A',
      'Contact': u.contact || 'N/A',
      'Email': u.email || 'N/A',
      'Status': u.status || 'N/A',
      'VIP': u.vip || 'No',
      'Wallet': u.wallet || '₹0',
      'Joined Date': u.date || 'N/A',
      'Address': u.address || 'N/A',
      'Referral Code': u.myreferal || 'N/A',
      'Refer By': u.refer_by || 'N/A'
    }));
  };

  const exportToPDF = async (option = 'current') => {
    setExporting(true);
    try {
      let dataToExport;
      let fileNameSuffix;
      
      if (option === 'all') {
        dataToExport = allUsers.length > 0 ? allUsers : users;
        fileNameSuffix = 'All_Users';
      } else {
        dataToExport = users;
        fileNameSuffix = 'Users_Page_' + page;
      }

      const data = getExportData(dataToExport);
      if (data.length === 0) {
        showToast("No users to export!", "error");
        setExporting(false);
        return;
      }

      const doc = new jsPDF('l', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      const title = option === 'all' ? 'All Users Report' : `Users Report - Page ${page}`;
      doc.text(title, pageWidth / 2, 15, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Exported: ${new Date().toLocaleString()}`, pageWidth / 2, 22, { align: 'center' });
      
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(`Total Users: ${data.length}`, 14, 30);
      
      const headers = Object.keys(data[0]);
      const rows = data.map(item => Object.values(item));

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 35,
        styles: { 
          fontSize: 6, 
          cellPadding: 2,
          textColor: [50, 50, 50]
        },
        headStyles: { 
          fillColor: [52, 73, 94], 
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: 'bold',
          halign: 'center',
          cellPadding: 2
        },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 22 },
          2: { cellWidth: 18 },
          3: { cellWidth: 25 },
          4: { cellWidth: 15 },
          5: { cellWidth: 12 },
          6: { cellWidth: 18 },
          7: { cellWidth: 18 },
          8: { cellWidth: 30 },
          9: { cellWidth: 18 },
          10: { cellWidth: 18 },
        },
        margin: { left: 10, right: 10 },
        pageBreak: 'auto',
        didDrawPage: function(data) {
          doc.setFontSize(7);
          doc.setTextColor(150, 150, 150);
          doc.text(
            `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
            pageWidth / 2,
            pageHeight - 8,
            { align: 'center' }
          );
        }
      });

      const fileName = `${fileNameSuffix}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      showToast(`Exported ${data.length} users to PDF!`);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showToast('Failed to export PDF. Please try again.', "error");
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async (option = 'current') => {
    setExporting(true);
    try {
      let dataToExport;
      let fileNameSuffix;
      
      if (option === 'all') {
        dataToExport = allUsers.length > 0 ? allUsers : users;
        fileNameSuffix = 'All_Users';
      } else {
        dataToExport = users;
        fileNameSuffix = 'Users_Page_' + page;
      }

      const data = getExportData(dataToExport);
      if (data.length === 0) {
        showToast("No users to export!", "error");
        setExporting(false);
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      
      ws['!cols'] = [
        { wch: 15 }, { wch: 25 }, { wch: 18 }, { wch: 30 },
        { wch: 15 }, { wch: 12 }, { wch: 18 }, { wch: 18 },
        { wch: 35 }, { wch: 18 }, { wch: 18 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Users');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const fileName = `${fileNameSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
      showToast(`Exported ${data.length} users to Excel!`);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showToast('Failed to export Excel. Please try again.', "error");
    } finally {
      setExporting(false);
    }
  };

  const exportSelected = () => {
    if (selected.size === 0) {
      showToast("Please select at least one user to export.", "error");
      return;
    }

    setExporting(true);
    try {
      const selectedUsers = users.filter(u => selected.has(u.id));
      const exportData = selectedUsers.map(u => ({
        'User ID': u.id || '',
        'Name': u.name || 'N/A',
        'Contact': u.contact || 'N/A',
        'Email': u.email || 'N/A',
        'Status': u.status || 'N/A',
        'VIP': u.vip || 'No',
        'Wallet': u.wallet || '₹0',
        'Joined Date': u.date || 'N/A',
        'Address': u.address || 'N/A',
        'Referral Code': u.myreferal || 'N/A',
        'Refer By': u.refer_by || 'N/A'
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 15 }, { wch: 25 }, { wch: 18 }, { wch: 30 },
        { wch: 15 }, { wch: 12 }, { wch: 18 }, { wch: 18 },
        { wch: 35 }, { wch: 18 }, { wch: 18 }
      ];
      XLSX.utils.book_append_sheet(wb, ws, 'Selected Users');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const fileName = `Selected_Users_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
      showToast(`Exported ${exportData.length} selected users!`);
    } catch (error) {
      console.error('Error exporting selected:', error);
      showToast('Failed to export selected users.', "error");
    } finally {
      setExporting(false);
    }
  };

  const mappedUsers = useMemo(() => users, [users]);
  const totalPages = Math.ceil(total / perPage);
  const pageIds = mappedUsers.map(u => u.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const somePageSelected = pageIds.some(id => selected.has(id)) && !allPageSelected;

  const toggleAll = () => { setSelected(prev => { const next = new Set(prev); allPageSelected ? pageIds.forEach(id => next.delete(id)) : pageIds.forEach(id => next.add(id)); return next; }); };
  const toggleOne = (id) => { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const clearSelection = () => setSelected(new Set());

  const handleDeleteSelected = async () => { 
    setActionLoading(true); 
    try { 
      await apiDeleteUsers(Array.from(selected)); 
      showToast(`${selected.size} user(s) deleted`); 
      clearSelection(); 
      setConfirmDelete(false); 
      fetchUsers(); 
    } catch (err) { 
      showToast(`Delete failed: ${err.message}`, "error"); 
    } finally { 
      setActionLoading(false); 
    } 
  };
  
  const openView = async (user) => { 
    setViewUser(user); 
    try { 
      const full = await apiGetUser(user.id); 
      if (full) setViewUser(full); 
    } catch {} 
  };

  const handleViewOrders = (user) => {
    if (canViewOrderHistory) {
      navigate("/dashboard/users/order-history", { 
        state: { 
          user: {
            id: user.id,
            name: user.name,
            contact: user.contact,
            email: user.email,
            rawStatus: user.rawStatus,
            rawVip: user.rawVip,
            address: user.address,
            wallet: user.wallet
          }
        }
      });
    } else {
      showToast("You don't have permission to view order history", "error");
    }
  };

  // Column widths
  const columnWidths = {
    checkbox: "32px",
    sn: "40px",
    status: "75px",
    date: "85px",
    pic: "44px",
    name: "120px",
    contact: "95px",
    wallet: "80px",
    vip: "50px",
    orders: "65px",
    actions: "60px"
  };

  return (
    <div style={{ minHeight: "100vh", background: theme.background, fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0}} @keyframes spin { to { transform: rotate(360deg); } } @keyframes slideUp { from{transform:translateY(16px);opacity:0} to{transform:translateY(0);opacity:1} } input::placeholder { color: ${theme.textMuted}; }`}</style>

      {/* Page Body */}
      <div style={{ padding: "16px 20px" }}>
        {/* Search + Actions */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "10px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "8px", padding: "8px 14px" }}>
            <span style={{ color: theme.primary, flexShrink: 0 }}><SearchIcon /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..." style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: "12px", color: theme.textPrimary, fontFamily: "'DM Sans', sans-serif", caretColor: theme.primary }} />
            {search && <div onClick={() => setSearch("")} style={{ cursor: "pointer", color: theme.textMuted, fontSize: "16px", lineHeight: 1 }}>×</div>}
          </div>
          <button onClick={fetchUsers} disabled={loading} style={{ padding: "0 14px", borderRadius: "8px", border: `1px solid ${theme.border}`, background: theme.surface, color: loading ? theme.textMuted : theme.textSecondary, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", height: "38px" }}><RefreshIcon /> Refresh</button>
          
          {/* ✅ Export Dropdown with Options */}
          <DropdownMenu
            theme={theme}
            trigger={
              <button 
                style={{ 
                  padding: "0 14px", 
                  borderRadius: "8px", 
                  border: `1px solid ${theme.border}`, 
                  background: theme.surface, 
                  color: theme.textPrimary, 
                  cursor: exporting ? "not-allowed" : "pointer", 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px", 
                  fontSize: "11px", 
                  height: "38px",
                  opacity: exporting ? 0.6 : 1,
                  fontWeight: 500
                }}
                disabled={exporting || users.length === 0}
              >
                <DownloadIcon /> Export <ChevronDownIcon />
              </button>
            }
          >
            <DropdownItem 
              onClick={() => exportToPDF('current')} 
              theme={theme}
              icon={<PDFIcon />}
            >
              PDF - Current Page ({users.length})
            </DropdownItem>
            <DropdownItem 
              onClick={() => exportToPDF('all')} 
              theme={theme}
              icon={<PDFIcon />}
            >
              PDF - All Data ({allUsers.length || total})
            </DropdownItem>
            <div style={{ borderTop: `1px solid ${theme.border}` }} />
            <DropdownItem 
              onClick={() => exportToExcel('current')} 
              theme={theme}
              icon={<ExcelIcon />}
            >
              Excel - Current Page ({users.length})
            </DropdownItem>
            <DropdownItem 
              onClick={() => exportToExcel('all')} 
              theme={theme}
              icon={<ExcelIcon />}
            >
              Excel - All Data ({allUsers.length || total})
            </DropdownItem>
            {selected.size > 0 && (
              <>
                <div style={{ borderTop: `1px solid ${theme.border}` }} />
                <DropdownItem 
                  onClick={exportSelected} 
                  theme={theme}
                  icon={<DownloadIcon />}
                >
                  Export Selected ({selected.size})
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </div>

        {error && (<div style={{ background: `${theme.error}1a`, border: `1px solid ${theme.error}40`, borderRadius: "8px", padding: "8px 14px", marginBottom: "12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}><span style={{ fontSize: "11px", color: theme.error }}>⚠ {error}</span><button onClick={fetchUsers} style={{ fontSize: "11px", color: theme.primary, background: "none", border: "none", cursor: "pointer" }}>Retry</button></div>)}

        {selected.size > 0 && (<div style={{ display: "flex", alignItems: "center", gap: "10px", background: theme.primaryLight, border: `1px solid ${theme.primary}40`, borderRadius: "8px", padding: "6px 14px", marginBottom: "12px" }}><span style={{ fontSize: "11px", color: theme.primary, fontWeight: 600 }}>{selected.size} user{selected.size > 1 ? "s" : ""} selected</span><div style={{ flex: 1 }} /><button onClick={clearSelection} style={{ padding: "3px 10px", borderRadius: "5px", border: `1px solid ${theme.border}`, background: theme.surface, color: theme.textSecondary, fontSize: "10px", cursor: "pointer" }}>Clear</button><button onClick={() => setConfirmDelete(true)} style={{ padding: "3px 10px", borderRadius: "5px", border: `1px solid ${theme.error}30`, background: `${theme.error}1a`, color: theme.error, fontSize: "10px", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}><TrashIcon /> Delete</button></div>)}

        {/* Users Table */}
        <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "12px", overflow: "hidden" }}>
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ background: theme.primaryLight }}>
                  <th style={{ padding: "10px 8px", width: columnWidths.checkbox, borderBottom: `1px solid ${theme.border}` }}>
                    <Checkbox checked={allPageSelected} indeterminate={somePageSelected} onChange={toggleAll} theme={theme} />
                  </th>
                  <ColHeader theme={theme} width={columnWidths.sn}>SN</ColHeader>
                  <ColHeader theme={theme} width={columnWidths.status}>Status</ColHeader>
                  <ColHeader theme={theme} width={columnWidths.date}>Date</ColHeader>
                  <ColHeader theme={theme} width={columnWidths.pic}>Pic</ColHeader>
                  <ColHeader theme={theme} width={columnWidths.name}>Name</ColHeader>
                  <ColHeader theme={theme} width={columnWidths.contact}>Contact</ColHeader>
                  <ColHeader theme={theme} width={columnWidths.wallet}>Wallet</ColHeader>
                  <ColHeader theme={theme} width={columnWidths.vip}>VIP</ColHeader>
                  <ColHeader theme={theme} width={columnWidths.orders}>Orders</ColHeader>
                  <ColHeader theme={theme} width={columnWidths.actions}>Actions</ColHeader>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: perPage }).map((_, i) => <SkeletonRow key={i} cols={11} theme={theme} />)
                ) : mappedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="11" style={{ padding: "40px", textAlign: "center", color: theme.textMuted, fontSize: "12px" }}>
                      {debouncedSearch ? "No users match your search" : "No users found"}
                    </td>
                  </tr>
                ) : (
                  mappedUsers.map((user, idx) => {
                    const isSel = selected.has(user.id); 
                    const isHov = hoveredRow === user.id;
                    return (
                      <tr 
                        key={user.id} 
                        onMouseEnter={() => setHoveredRow(user.id)} 
                        onMouseLeave={() => setHoveredRow(null)} 
                        style={{ 
                          background: isSel ? theme.primaryLight : isHov ? `${theme.primary}08` : "transparent",
                          borderBottom: idx < mappedUsers.length - 1 ? `1px solid ${theme.border}` : "none" 
                        }}
                      >
                        <td style={{ padding: "10px 8px" }}>
                          <Checkbox checked={isSel} onChange={() => toggleOne(user.id)} theme={theme} />
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          <span style={{ fontSize: "11px", color: theme.textMuted }}>{user.sn}</span>
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          <StatusBadge status={user.status} theme={theme} />
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          <span style={{ fontSize: "10px", color: theme.textMuted, whiteSpace: "nowrap" }}>{user.date}</span>
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          <SmartAvatar 
                            name={user.name} 
                            src={user.avatar} 
                            size={28} 
                            theme={theme} 
                            borderColor={theme.border}
                          />
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          <span style={{ fontSize: "11.5px", fontWeight: 600, color: theme.textPrimary }}>{user.name}</span>
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          <span style={{ fontSize: "11px", color: theme.textSecondary }}>{user.contact}</span>
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          <span style={{ fontSize: "11px", fontWeight: 600, color: theme.textPrimary }}>{user.wallet}</span>
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          {user.vip === "Yes" ? (
                            <span style={{ 
                              fontSize: "9px", 
                              fontWeight: 700, 
                              color: theme.warning, 
                              background: `${theme.warning}1a`, 
                              border: `1px solid ${theme.warning}40`, 
                              borderRadius: "4px", 
                              padding: "2px 6px" 
                            }}>
                              YES
                            </span>
                          ) : (
                            <span style={{ fontSize: "11px", color: theme.textMuted }}>No</span>
                          )}
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          {canViewOrderHistory && (
                            <button 
                              onClick={() => handleViewOrders(user)} 
                              style={{ 
                                background: "none", 
                                border: "none", 
                                cursor: "pointer", 
                                color: theme.textMuted, 
                                display: "flex", 
                                alignItems: "center", 
                                gap: "4px", 
                                fontSize: "10px" 
                              }}
                            >
                              <ShoppingBagIcon />
                              <span>Orders</span>
                            </button>
                          )}
                          {!canViewOrderHistory && (
                            <span style={{ fontSize: "10px", color: theme.textMuted, opacity: 0.5 }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div 
                              onClick={() => openView(user)} 
                              onMouseEnter={() => setHoveredBtn(`v-${user.id}`)} 
                              onMouseLeave={() => setHoveredBtn(null)} 
                              style={{ 
                                cursor: "pointer", 
                                color: hoveredBtn === `v-${user.id}` ? theme.primary : theme.textMuted 
                              }}
                            >
                              <EyeIcon />
                            </div>
                            {canEditUser && (
                              <div 
                                onClick={() => handleEditNavigation(user)} 
                                onMouseEnter={() => setHoveredBtn(`e-${user.id}`)} 
                                onMouseLeave={() => setHoveredBtn(null)} 
                                style={{ 
                                  cursor: "pointer", 
                                  color: hoveredBtn === `e-${user.id}` ? theme.success : theme.textMuted 
                                }}
                              >
                                <EditIcon />
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between", 
            padding: "10px 16px", 
            borderTop: `1px solid ${theme.border}`, 
            background: theme.primaryLight 
          }}>
            <span style={{ fontSize: "10px", color: theme.textMuted }}>
              {loading ? "Loading…" : `Showing ${Math.min((page-1)*perPage+1, total)}–${Math.min(page*perPage, total)} of ${total} users`}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button 
                onClick={() => setPage(p => Math.max(1, p-1))} 
                disabled={page === 1 || loading} 
                style={{ 
                  width: "26px", 
                  height: "26px", 
                  borderRadius: "5px", 
                  border: `1px solid ${theme.border}`, 
                  background: theme.surface, 
                  color: page === 1 ? theme.textMuted : theme.textSecondary, 
                  cursor: page === 1 ? "not-allowed" : "pointer", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center" 
                }}
              >
                <ChevronLeftIcon />
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p; 
                if (totalPages <= 5) {
                  p = i + 1;
                } else if (page <= 3) {
                  p = i + 1;
                } else if (page >= totalPages - 2) {
                  p = totalPages - 4 + i;
                } else {
                  p = page - 2 + i;
                }
                return p <= totalPages && (
                  <button 
                    key={p} 
                    onClick={() => setPage(p)} 
                    style={{ 
                      width: "26px", 
                      height: "26px", 
                      borderRadius: "5px", 
                      border: page === p ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`, 
                      background: page === p ? theme.primaryLight : theme.surface, 
                      color: page === p ? theme.primary : theme.textSecondary, 
                      fontSize: "10px", 
                      fontWeight: page === p ? 700 : 400, 
                      cursor: "pointer" 
                    }}
                  >
                    {p}
                  </button>
                );
              })}
              
              <button 
                onClick={() => setPage(p => Math.min(totalPages, p+1))} 
                disabled={page === totalPages || loading} 
                style={{ 
                  width: "26px", 
                  height: "26px", 
                  borderRadius: "5px", 
                  border: `1px solid ${theme.border}`, 
                  background: theme.surface, 
                  color: page === totalPages ? theme.textMuted : theme.textSecondary, 
                  cursor: page === totalPages ? "not-allowed" : "pointer", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center" 
                }}
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* View User Modal */}
      {viewUser && (
        <div onClick={() => setViewUser(null)} style={{ position:"fixed",inset:0,zIndex:200,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius:"12px", padding:"20px", width:"320px" }}>
            <div style={{ display:"flex",alignItems:"center",gap:"12px",marginBottom:"16px" }}>
              <SmartAvatar 
                name={viewUser.name} 
                src={viewUser.avatar} 
                size={44} 
                theme={theme} 
                borderColor={theme.border}
              />
              <div>
                <h3 style={{ margin:0,fontSize:"15px",fontWeight:700,color: theme.textPrimary }}>{viewUser.name}</h3>
                <p style={{ margin:"2px 0 0",fontSize:"10px",color: theme.textMuted }}>ID: {viewUser.id}</p>
              </div>
            </div>
            {[
              ["Status", <StatusBadge status={viewUser.status} theme={theme} />],
              ["Contact", viewUser.contact],
              ["Email", viewUser.email],
              ["Joined", viewUser.date],
              ["Wallet", viewUser.wallet],
              ["VIP", viewUser.vip]
            ].map(([label,value]) => (
              <div key={label} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 0",borderBottom: `1px solid ${theme.border}` }}>
                <span style={{ fontSize:"10px",color: theme.textMuted }}>{label}</span>
                {typeof value==="string" ? <span style={{ fontSize:"11px",fontWeight:600,color: theme.textPrimary }}>{value}</span> : value}
              </div>
            ))}
            <button onClick={()=>setViewUser(null)} style={{ width:"100%",marginTop:"16px",padding:"8px",borderRadius:"6px",border:"none",background: theme.primary,color: "#fff",fontSize:"11px",fontWeight:600,cursor:"pointer" }}>Close</button>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div onClick={()=>setConfirmDelete(false)} style={{ position:"fixed",inset:0,zIndex:300,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ background: theme.surface, border:`1px solid ${theme.error}30`,borderRadius:"12px",padding:"20px",width:"300px" }}>
            <div style={{ width:"36px",height:"36px",borderRadius:"10px",background:`${theme.error}1a`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:"12px" }}><TrashIcon /></div>
            <h3 style={{ margin:"0 0 6px",fontSize:"14px",fontWeight:700,color: theme.textPrimary }}>Delete {selected.size} user{selected.size>1?"s":""}?</h3>
            <p style={{ margin:"0 0 16px",fontSize:"11px",color: theme.textMuted,lineHeight:1.5 }}>This action is permanent and cannot be undone.</p>
            <div style={{ display:"flex",gap:"8px" }}>
              <button onClick={()=>setConfirmDelete(false)} style={{ flex:1,padding:"8px",borderRadius:"6px",border:`1px solid ${theme.border}`,background: theme.surface,color: theme.textSecondary,fontSize:"11px",fontWeight:600,cursor:"pointer" }}>Cancel</button>
              <button onClick={handleDeleteSelected} disabled={actionLoading} style={{ flex:1,padding:"8px",borderRadius:"6px",border:"none",background: `${theme.error}1a`,color: theme.error,fontSize:"11px",fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:"5px" }}>{actionLoading ? <><SpinnerIcon /> Deleting…</> : "Yes, Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={()=>setToast(null)} theme={theme} />}
    </div>
  );
};

export default UsersTable;